import React, { useEffect, useMemo, useState } from "react";
import { getExchangeRate } from "../api/nbgCurrencyApi";

const fmt = (value) =>
  Number(value || 0).toLocaleString("ka-GE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getNumeric = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTypeLabel = (row) => {
  const raw = String(row?.Resource_Type || row?.resource_type || row?.Discipline || "").toUpperCase();
  if (raw.includes("MATERIAL")) return "Material";
  if (raw.includes("LABOR") || raw.includes("LABOUR")) return "Labour";
  if (raw.includes("EQUIPMENT")) return "Equipment";
  return "Other";
};

const getRowTotal = (row) => {
  const qty = getNumeric(row?.Qty_Per_Work_Unit ?? row?.qty_per_work_unit ?? row?.Qty ?? row?.quantity ?? 1);
  const rate = getNumeric(
    row?.Total_Price ??
      row?.total_price ??
      row?.Total ??
      row?.total ??
      row?.Unit_Rate ??
      row?.unit_rate ??
      row?.UnitPrice ??
      row?.unit_price ??
      row?.mat_total ??
      row?.salary_total ??
      row?.transport_total ??
      0
  );

  if (row?.Total_Price || row?.total_price || row?.Total || row?.total) {
    return rate;
  }

  const totalFromSpecificFields = getNumeric(
    row?.mat_total ?? row?.salary_total ?? row?.transport_total ?? row?.totalPrice ?? row?.total_price ?? 0
  );

  if (totalFromSpecificFields > 0) {
    return totalFromSpecificFields;
  }

  return qty > 0 ? rate * qty : rate;
};

// A parent table (root / chapter) only mirrors the sum of its children,
// so counting both double-counts. Keep only the leaf tables.
const leafTablesForProject = (allTables, projectId) => {
  const inProject = (allTables || []).filter(
    (t) => String(t?.project_id || "") === String(projectId || "")
  );
  const parentIds = new Set(
    inProject.map((t) => t?.parent_table_id).filter(Boolean).map(String)
  );
  const leaves = inProject.filter((t) => !parentIds.has(String(t.id)));
  return leaves.length > 0 ? leaves : inProject;
};

const buildAnalytics = (projectTables, rowsByTable) => {
  const rows = [];
  (projectTables || []).forEach((table) => {
    const tableRows = Array.isArray(rowsByTable?.[table.id]) ? rowsByTable[table.id] : [];
    tableRows.forEach((row) => rows.push({ ...row, _tableName: table?.name || table?.display_name || "BOQ" }));
  });

  const totals = rows.reduce(
    (acc, row) => {
      const total = getRowTotal(row);
      const type = getTypeLabel(row);
      acc.total += total;
      if (type === "Material") acc.material += total;
      if (type === "Labour") acc.labour += total;
      if (type === "Equipment") acc.equipment += total;
      return acc;
    },
    { total: 0, material: 0, labour: 0, equipment: 0 }
  );

  const topRows = [...rows]
    .sort((a, b) => getRowTotal(b) - getRowTotal(a))
    .slice(0, 5);

  return {
    totalTables: projectTables.length,
    totalRows: rows.length,
    totalCost: totals.total,
    materialCost: totals.material,
    labourCost: totals.labour,
    equipmentCost: totals.equipment,
    topRows,
  };
};

export default function AnalyticsPage({ project, token, tables = [], rowsByTable = {}, loadRows, onGoToBOQ, projects = [] }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comparisonProjectId, setComparisonProjectId] = useState("");

  const currentProjectTables = useMemo(
    () => leafTablesForProject(tables, project?.id),
    [tables, project?.id]
  );

  const availableProjects = useMemo(
    () => (projects || []).filter((item) => String(item?.id || "") !== String(project?.id || "")),
    [projects, project?.id]
  );

  useEffect(() => {
    if (!comparisonProjectId && availableProjects.length > 0) {
      setComparisonProjectId(String(availableProjects[0].id));
    }
  }, [availableProjects, comparisonProjectId]);

  const comparisonProject = useMemo(
    () => availableProjects.find((item) => String(item?.id || "") === String(comparisonProjectId)) || null,
    [availableProjects, comparisonProjectId]
  );

  const comparisonProjectTables = useMemo(
    () => (comparisonProject ? leafTablesForProject(tables, comparisonProject.id) : []),
    [comparisonProject, tables]
  );

  const currentHasRows = useMemo(() => {
    return currentProjectTables.some((table) => {
      const tableRows = Array.isArray(rowsByTable?.[table.id]) ? rowsByTable[table.id] : [];
      return tableRows.length > 0;
    });
  }, [currentProjectTables, rowsByTable]);

  const comparisonHasRows = useMemo(() => {
    return comparisonProjectTables.some((table) => {
      const tableRows = Array.isArray(rowsByTable?.[table.id]) ? rowsByTable[table.id] : [];
      return tableRows.length > 0;
    });
  }, [comparisonProjectTables, rowsByTable]);

  useEffect(() => {
    let isMounted = true;

    const loadAllRows = async () => {
      const tablesToLoad = [];
      if (token && project?.id && currentProjectTables.length > 0 && !currentHasRows) {
        tablesToLoad.push(...currentProjectTables);
      }

      if (token && comparisonProject?.id && comparisonProjectTables.length > 0 && !comparisonHasRows) {
        tablesToLoad.push(...comparisonProjectTables);
      }

      if (tablesToLoad.length === 0) {
        if (isMounted) {
          setLoading(false);
          setError("");
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError("");
        }
        await Promise.all(tablesToLoad.map((table) => loadRows?.(table.id)));
      } catch (err) {
        if (isMounted) {
          setError(err?.message || "Unable to load analytics data.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAllRows();
    return () => {
      isMounted = false;
    };
  }, [token, project?.id, currentProjectTables, currentHasRows, comparisonProject?.id, comparisonProjectTables, comparisonHasRows, loadRows]);

  const currentAnalytics = useMemo(() => buildAnalytics(currentProjectTables, rowsByTable), [currentProjectTables, rowsByTable]);
  const comparisonAnalytics = useMemo(() => buildAnalytics(comparisonProjectTables, rowsByTable), [comparisonProjectTables, rowsByTable]);

  // BOQ amounts are stored in GEL (the base/catalog currency stays GEL). Show
  // them in the project's current primary currency, converting GEL -> primary
  // with the NBG rate. Works for any primary/secondary pair.
  const primaryCurrency = project?.real_currency || "GEL";
  const [gelRate, setGelRate] = useState(null); // primary units per 1 GEL
  useEffect(() => {
    let cancelled = false;
    if (primaryCurrency === "GEL") {
      setGelRate(1);
      return undefined;
    }
    setGelRate(null);
    getExchangeRate("GEL", primaryCurrency)
      .then((rate) => { if (!cancelled && rate > 0) setGelRate(rate); })
      .catch(() => { if (!cancelled) setGelRate(null); });
    return () => { cancelled = true; };
  }, [primaryCurrency]);

  // Until the rate arrives (or if NBG is unreachable) fall back to GEL so the
  // label never claims a conversion that didn't happen.
  const canConvert = gelRate != null;
  const currency = canConvert ? primaryCurrency : "GEL";
  const displayFactor = canConvert ? gelRate : 1;
  const toDisplay = (gelAmount) => (Number(gelAmount) || 0) * displayFactor;

  const totalDelta = currentAnalytics.totalCost - comparisonAnalytics.totalCost;
  const totalDeltaPct = comparisonAnalytics.totalCost > 0 ? (totalDelta / comparisonAnalytics.totalCost) * 100 : 0;

  // Cost per m² — a quick sanity check against wildly wrong estimates
  // (a normal residential build lands roughly in the hundreds-to-low-
  // thousands per m²; anything in the tens of thousands is worth a look).
  const areaValue = Number(project?.area) || 0;
  const costPerArea = areaValue > 0 ? toDisplay(currentAnalytics.totalCost) / areaValue : null;
  const perArea = (gelValue) => (areaValue > 0 ? `${fmt(toDisplay(gelValue) / areaValue)} ${currency}/m²` : null);

  const summaryCards = [
    { label: "Detail sheets", value: currentAnalytics.totalTables, accent: "var(--an-blue)" },
    { label: "Resource rows", value: currentAnalytics.totalRows, accent: "var(--an-violet)" },
    { label: "Total cost", value: `${fmt(toDisplay(currentAnalytics.totalCost))} ${currency}`, accent: "var(--an-green)" },
    {
      label: "Cost / m²",
      value: costPerArea != null ? `${fmt(costPerArea)} ${currency}` : "— (no area set)",
      accent: "var(--an-amber)",
    },
  ];

  const splitRows = [
    { label: "Material", value: currentAnalytics.materialCost, color: "var(--an-green)" },
    { label: "Labour", value: currentAnalytics.labourCost, color: "var(--an-blue)" },
    { label: "Equipment", value: currentAnalytics.equipmentCost, color: "var(--an-amber)" },
  ];

  const comboCost = currentAnalytics.totalCost + comparisonAnalytics.totalCost;
  const currentShare = comboCost > 0 ? (currentAnalytics.totalCost / comboCost) * 100 : 50;

  return (
    <div className="analytics-page">
      <div className="analytics-head">
        <div>
          <div className="analytics-title">Advanced analytics</div>
          <div className="analytics-subtitle">BOQ insights for {project?.name || "this project"}</div>
        </div>
        <div className="analytics-head-actions">
          <label className="analytics-compare-label">
            Compare with
            <select
              className="analytics-select"
              value={comparisonProjectId}
              onChange={(event) => setComparisonProjectId(event.target.value)}
            >
              <option value="">Select project</option>
              {availableProjects.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          {typeof onGoToBOQ === "function" && (
            <button type="button" className="primary analytics-open-boq" onClick={onGoToBOQ}>
              Open BOQ
            </button>
          )}
        </div>
      </div>

      {error && <div className="analytics-error">{error}</div>}

      <div className="analytics-cards">
        {summaryCards.map((card) => (
          <div key={card.label} className="analytics-card">
            <div className="analytics-card-label">{card.label}</div>
            <div className="analytics-card-value" style={{ color: card.accent }}>{card.value}</div>
          </div>
        ))}
      </div>

      {comparisonProject && (
        <div className="analytics-card">
          <div className="analytics-card-title">Project comparison</div>
          <div className="analytics-compare-row">
            <span><strong>{project?.name || "Current project"}</strong>: {fmt(toDisplay(currentAnalytics.totalCost))} {currency}</span>
            <span><strong>{comparisonProject.name}</strong>: {fmt(toDisplay(comparisonAnalytics.totalCost))} {currency}</span>
          </div>
          <div className="analytics-compare-delta">
            Difference:{" "}
            <span className={totalDelta >= 0 ? "is-up" : "is-down"}>
              {totalDelta >= 0 ? "+" : ""}{fmt(toDisplay(totalDelta))} {currency} ({totalDeltaPct >= 0 ? "+" : ""}{fmt(totalDeltaPct)}%)
            </span>
          </div>
          <div className="analytics-bar analytics-bar--split">
            <div style={{ width: `${Math.max(4, Math.min(96, currentShare))}%`, background: "var(--an-green)" }} />
            <div style={{ width: `${Math.max(4, Math.min(96, 100 - currentShare))}%`, background: "var(--an-blue)" }} />
          </div>
        </div>
      )}

      <div className="analytics-card">
        <div className="analytics-card-title">Cost split</div>
        <div className="analytics-split">
          {splitRows.map((item) => {
            const pct = currentAnalytics.totalCost > 0 ? (item.value / currentAnalytics.totalCost) * 100 : 0;
            return (
              <div key={item.label}>
                <div className="analytics-split-head">
                  <span>{item.label}</span>
                  <span className="analytics-muted">
                    {fmt(toDisplay(item.value))} {currency}
                    {perArea(item.value) ? ` · ${perArea(item.value)}` : ""}
                  </span>
                </div>
                <div className="analytics-bar">
                  <div style={{ width: `${pct}%`, background: item.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="analytics-card">
        <div className="analytics-card-title">Top cost items</div>
        {loading && currentAnalytics.totalRows === 0 ? (
          <div className="analytics-muted">Loading BOQ data…</div>
        ) : currentAnalytics.topRows.length === 0 ? (
          <div className="analytics-muted">No rows available yet.</div>
        ) : (
          <div className="analytics-top">
            {currentAnalytics.topRows.map((row, index) => {
              const title = row?.Material_Name_GEO || row?.Material_Name_ENG || row?.Material_Name_RUS || row?.title || row?.name || `Row ${index + 1}`;
              const total = getRowTotal(row);
              return (
                <div key={`${row?.id || index}-${row?._tableName || "table"}`} className="analytics-top-item">
                  <div className="analytics-top-main">
                    <div className="analytics-top-name">{title}</div>
                    <div className="analytics-top-sheet">{row?._tableName || "BOQ"}</div>
                  </div>
                  <div className="analytics-top-value">{fmt(toDisplay(total))} {currency}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
